import { WebSocketClient } from "@infrastructure/clients/ws/clients/base/websocket.client";

export interface MQTTSubscribeOptions {
  topic: string;
  qos?: 0 | 1 | 2;
  onMessage: (data: { topic: string; payload: any; qos: number }) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
  reconnect?: boolean;
  reconnectDelay?: number;
}

export abstract class MQTTClient extends WebSocketClient {
  private messageId = 0;
  private topicSubscriptions = new Map<string, Set<(data: any) => void>>();

  async connect(endpoint: string): Promise<void> {
    await super.connect(endpoint);
    this.setupMQTTHandlers();
  }

  private setupMQTTHandlers(): void {
    // Переопределяем обработчик сообщений для MQTT протокола
    if (this.ws) {
      const originalOnMessage = this.ws.onmessage;
      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMQTTMessage(message);
        } catch (error) {
          console.error("[MQTT] Failed to parse message:", error);
        }
      };
    }
  }

  subscribe(topic: string, options: MQTTSubscribeOptions): () => void {
    const messageId = ++this.messageId;
    const qos = options.qos || 0;

    const subscribeMessage = {
      type: "subscribe",
      messageId,
      topics: [{ topic, qos }],
    };

    this.send(subscribeMessage);

    const handler = (data: { topic: string; payload: any; qos: number }) => {
      if (data.topic === topic || this.topicMatches(topic, data.topic)) {
        try {
          options.onMessage(data);
        } catch (error) {
          options.onError?.(error as Error);
        }
      }
    };

    // Сохраняем обработчик для этого топика
    if (!this.topicSubscriptions.has(topic)) {
      this.topicSubscriptions.set(topic, new Set());
    }
    this.topicSubscriptions.get(topic)!.add(handler);

    return () => {
      this.topicSubscriptions.get(topic)?.delete(handler);
      if (this.topicSubscriptions.get(topic)?.size === 0) {
        this.topicSubscriptions.delete(topic);
        const unsubscribeMessage = {
          type: "unsubscribe",
          messageId: ++this.messageId,
          topics: [topic],
        };
        this.send(unsubscribeMessage);
      }
    };
  }

  publish(topic: string, payload: any, qos: 0 | 1 | 2 = 0): void {
    const message = {
      type: "publish",
      messageId: ++this.messageId,
      topic,
      payload,
      qos,
    };
    this.send(message);
  }

  private handleMQTTMessage(message: any): void {
    if (message.type === "publish" && message.topic) {
      const handlers = Array.from(this.topicSubscriptions.entries())
        .filter(([subscribedTopic]) => this.topicMatches(subscribedTopic, message.topic))
        .flatMap(([, handlers]) => Array.from(handlers));

      handlers.forEach((handler) => {
        handler({
          topic: message.topic,
          payload: message.payload,
          qos: message.qos || 0,
        });
      });
    }
  }

  private topicMatches(subscribedTopic: string, receivedTopic: string): boolean {
    // Простая поддержка wildcards: + для одного уровня, # для множественных
    const subscribedParts = subscribedTopic.split("/");
    const receivedParts = receivedTopic.split("/");

    if (subscribedParts.length !== receivedParts.length && !subscribedTopic.includes("#")) {
      return false;
    }

    for (let i = 0; i < subscribedParts.length; i++) {
      const subPart = subscribedParts[i];
      const recPart = receivedParts[i];

      if (subPart === "#") {
        return true; // Multi-level wildcard matches everything after
      }
      if (subPart === "+") {
        continue; // Single-level wildcard matches any value
      }
      if (subPart !== recPart) {
        return false;
      }
    }

    return true;
  }

  disconnect(): void {
    this.topicSubscriptions.clear();
    super.disconnect();
  }
}

